/**
 * Mastodon direct instance API for fetching profile posts and threads
 *
 * Fetches posts directly from a user's home Mastodon instance for better
 * freshness and latency, with fallback to GraphQL when needed.
 */
import type { MastodonStatus, MastodonContext, MastodonQuote, MastodonPoll } from '@/types/mastodon'
import type { Post, PostEmbed, PostFacet, PostPoll, PostsPage } from '@/types/posts'
import type { Thread, ThreadPost } from '@/types/thread'
import type { MastodonList } from '@/types/lists'
import {
    type MastodonSession,
    type MastodonAccount,
    ensureValidSession,
    instanceUrlFromHandle,
    lookupAccountOnInstance,
    parseLinkHeader,
    normalizeAcct,
    stripHTML,
} from '@/lib/mastodon'
import { canonicalizeInstanceOrigin } from '@/lib/mastodonFetch'
import { stripQuoteInline } from '@/services/postsService'
import { isSafeHostname } from '@/lib/apUtils'
import { contentWarningFromMastodon } from '@/lib/contentLabels'
import { deriveATBridgeFromAcct, isTrustedBridgeOrigin } from '@/lib/apBridgedIdentity'

const FETCH_TIMEOUT_MS = 10_000
const DEFAULT_LIMIT = 25
/**
 * Page size for the pinned-statuses request — the API's `limit` cap.
 * `max_pinned_statuses` defaults to 5, so a single page always suffices in
 * practice and we deliberately do not follow the `Link: rel="next"` header.
 */
const PINNED_STATUSES_LIMIT = 40
const ACCOUNT_CACHE_MAX = 100

interface CachedAccount {
    id: string
    displayName?: string
    avatarUrl?: string
    resolvedHandle?: string
}

/** In-memory cache for account lookups to avoid redundant requests during pagination */
const accountCache = new Map<string, CachedAccount>()

function setCachedAccount(key: string, account: CachedAccount): void {
    if (accountCache.size >= ACCOUNT_CACHE_MAX) {
        const firstKey = accountCache.keys().next().value
        if (firstKey !== undefined) accountCache.delete(firstKey)
    }
    accountCache.set(key, account)
}

/**
 * Whether `a` and `b` canonicalize to the same origin. A canonicalization
 * failure on either side is treated as "not the same origin" — the caller
 * only uses this to decide whether an account lookup needs a fully
 * federation-qualified `acct` or can use the local username.
 */
function isSameCanonicalOrigin(a: string, b: string): boolean {
    try {
        return canonicalizeInstanceOrigin(a).origin === canonicalizeInstanceOrigin(b).origin
    } catch {
        return false
    }
}

/**
 * Fetch statuses for an account directly from a Mastodon instance (unauthenticated).
 *
 * @returns Object with statuses array and optional next page URL
 */
export async function fetchMastodonStatuses(
    instanceUrl: string,
    accountId: string,
    options?: {
        maxId?: string
        limit?: number
        accessToken?: string
        onlyMedia?: boolean
        onlyReblogs?: boolean
        /**
         * Fetch the account's pinned ("featured") statuses instead of its timeline.
         * Deliberately unfiltered — Mastodon web's featured request sends no
         * exclude_replies/only_media either, so a pinned reply is still returned.
         */
        pinned?: boolean
    }
): Promise<{ statuses: MastodonStatus[]; nextUrl?: string }> {
    const hostname = new URL(instanceUrl).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const params = new URLSearchParams()
    if (options?.pinned) {
        // Pinned list: request the API's max page size and apply no other filters.
        params.set('limit', String(PINNED_STATUSES_LIMIT))
        params.set('pinned', 'true')
    } else {
        params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
        if (options?.maxId) {
            params.set('max_id', options.maxId)
        }
        if (options?.onlyReblogs) {
            params.set('only_reblogs', 'true')
        } else {
            // Exclude replies for profile posts feed (applies to both regular and media-only fetches)
            // Not applicable when fetching only reblogs.
            params.set('exclude_replies', 'true')
        }
        if (options?.onlyMedia) {
            params.set('only_media', 'true')
        }
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instanceUrl}/api/v1/accounts/${encodeURIComponent(accountId)}/statuses?${params}`
        const headers: Record<string, string> = {}
        if (options?.accessToken) {
            headers.Authorization = `Bearer ${options.accessToken}`
        }
        const res = await fetch(url, { headers, signal: controller.signal })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        return { statuses, nextUrl }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the authenticated user's Mastodon home timeline.
 *
 * Requires a valid MastodonSession (auto-refreshes expired tokens).
 * Uses the same pagination pattern as fetchMastodonStatuses (Link header → cursor).
 */
export type MastodonTimelineType = 'home' | 'local' | 'federated'

export async function fetchMastodonTimeline(
    mastodonSession: MastodonSession,
    options?: { cursor?: string; limit?: number; timeline?: MastodonTimelineType }
): Promise<PostsPage> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const timelineType = options?.timeline ?? 'home'
    let timelinePath: string
    if (timelineType === 'home') {
        timelinePath = '/api/v1/timelines/home'
    } else {
        if (timelineType === 'local') {
            params.set('local', 'true')
        }
        timelinePath = '/api/v1/timelines/public'
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}${timelinePath}?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapMastodonStatus(s, session.instance_url))

        // Extract cursor from the next page URL's max_id param.
        // Only trust URLs from the same origin to prevent token leakage via crafted Link headers.
        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                // If URL parsing fails, use the last status ID as cursor
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the authenticated user's favourited statuses.
 *
 * Uses the same session validation, SSRF protection, and pagination pattern
 * as fetchMastodonTimeline.
 */
export async function fetchMastodonFavourites(
    mastodonSession: MastodonSession,
    options?: { cursor?: string; limit?: number }
): Promise<PostsPage> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}/api/v1/favourites?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const posts = statuses.map(s => mapMastodonStatus(s, session.instance_url))

        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Resolve a Mastodon account acct field to a display handle.
 *
 * Bridged AT→AP accounts appear in the Mastodon API with a Bridgy Fed handle
 * (e.g. "whiteside.io@bsky.brid.gy"). In that case, the username field contains
 * the clean Bluesky handle (e.g. "whiteside.io") which we prefer.
 *
 * AP-native accounts use normalizeAcct to add @instance for local users.
 */
function resolveAcctHandle(
    acct: string,
    instanceUrl: string,
    username?: string,
    isTrustedOrigin = true
): string {
    const bridge = deriveATBridgeFromAcct(acct, username, isTrustedOrigin)
    if (bridge.isBridged) return bridge.handle
    // AP-native: normalizeAcct adds @instance for local users
    return normalizeAcct(acct, instanceUrl)
}

/**
 * Extract link facets from Mastodon HTML content.
 * Parses <a href="..."> elements and maps them to AT Protocol-style PostFacet
 * byte-range annotations so RichText can render them as clickable links.
 */
function extractMastodonFacets(htmlContent: string, strippedText: string): PostFacet[] {
    if (!strippedText) return []
    const facets: PostFacet[] = []
    const encoder = new TextEncoder()
    const anchorRegex = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi
    let match: RegExpExecArray | null
    let searchStart = 0
    while ((match = anchorRegex.exec(htmlContent)) !== null) {
        const attrStr = match[1]
        const innerHtml = match[2]
        const hrefMatch = attrStr.match(/\bhref=["']([^"']+)["']/)
        if (!hrefMatch) continue
        const href = hrefMatch[1]
        if (!href.startsWith('http://') && !href.startsWith('https://')) continue
        const displayText = stripHTML(innerHtml)
        if (!displayText) continue
        const idx = strippedText.indexOf(displayText, searchStart)
        if (idx === -1) continue
        const byteStart = encoder.encode(strippedText.slice(0, idx)).length
        const byteEnd = encoder.encode(strippedText.slice(0, idx + displayText.length)).length
        if (byteEnd > byteStart) {
            const isHashtag = displayText.startsWith('#') || /\/tags?\//.test(href)
            if (isHashtag) {
                const tagName = displayText.startsWith('#') ? displayText.slice(1) : displayText
                if (!tagName) continue
                facets.push({
                    index: { byteStart, byteEnd },
                    features: [{ type: 'tag', tag: tagName }],
                })
            } else {
                facets.push({
                    index: { byteStart, byteEnd },
                    features: [{ type: 'link', uri: href }],
                })
            }
            searchStart = idx + displayText.length
        }
    }
    return facets
}

/**
 * Map a Mastodon status to our normalized Post type.
 *
 * fetchedInstanceUrl is the instance the status JSON was fetched from and is the
 * only source for a local-form `acct`'s domain; `status.uri` is remote-controlled
 * and must never be consulted for identity.
 */
export function mapMastodonStatus(
    status: MastodonStatus,
    fetchedInstanceUrl: string,
    profileAuthor?: Post['author'],
    isTrustedOrigin = true
): Post {
    // For boosts, the reblogged status is the actual content
    const isBoost = !!status.reblog
    // The timeline entry's delivering account/id, captured from the OUTER
    // status before the boost reassignment below — followed-hashtag
    // attribution must test the booster, not the content author.
    const timelineAccountId = status.account.id
    const timelineStatusId = status.id
    const contentStatus = status.reblog ?? status

    // Only strip quote-inline fallback text when we'll actually render the embed
    const isAcceptedQuote =
        contentStatus.quote?.state === 'accepted' && !!contentStatus.quote.quoted_status
    const htmlContent = isAcceptedQuote
        ? stripQuoteInline(contentStatus.content)
        : contentStatus.content
    const text = stripHTML(htmlContent)
    const facets = extractMastodonFacets(htmlContent, text)

    const bridge = deriveATBridgeFromAcct(
        contentStatus.account.acct,
        contentStatus.account.username,
        isTrustedOrigin
    )
    const normalizedHandle = bridge.isBridged
        ? bridge.handle
        : normalizeAcct(contentStatus.account.acct, fetchedInstanceUrl)

    const author: Post['author'] = {
        did: '',
        handle: normalizedHandle,
        displayHandle: bridge.isBridged ? bridge.handle : normalizedHandle,
        displayName: contentStatus.account.display_name || undefined,
        avatarUrl: contentStatus.account.avatar || undefined,
        bridgeStatus: bridge.isBridged
            ? {
                  isBridged: true,
                  sourceProtocol: 'atprotocol',
                  activityPubHandle: bridge.activityPubHandle,
              }
            : undefined,
    }

    let boostedBy: Post['boostedBy']
    if (isBoost) {
        const boosterBridge = deriveATBridgeFromAcct(
            status.account.acct,
            status.account.username,
            isTrustedOrigin
        )
        const boosterNormalized = boosterBridge.isBridged
            ? boosterBridge.handle
            : normalizeAcct(status.account.acct, fetchedInstanceUrl)
        const boosterHandle = profileAuthor?.handle ?? boosterNormalized
        boostedBy = {
            handle: boosterHandle,
            displayHandle:
                profileAuthor?.displayHandle ??
                (boosterBridge.isBridged ? boosterBridge.handle : boosterNormalized),
            displayName: profileAuthor?.displayName ?? (status.account.display_name || undefined),
            avatarUrl: profileAuthor?.avatarUrl ?? (status.account.avatar || undefined),
        }
    }

    // Extract quoted post from Mastodon 4.4+ quote field
    const quotedPost = isAcceptedQuote
        ? mapMastodonQuote(contentStatus.quote, fetchedInstanceUrl, isTrustedOrigin)
        : undefined

    return {
        uri: contentStatus.uri,
        cid: undefined,
        rkey: undefined,
        mastodonStatusId: contentStatus.id,
        mastodonTimelineAccountId: timelineAccountId,
        mastodonTimelineStatusId: timelineStatusId,
        mastodonTags: (contentStatus.tags ?? []).map(t => t.name),
        text,
        facets: facets.length > 0 ? facets : undefined,
        // Use boost event time for feed ordering (matches GraphQL boostedAt behavior)
        createdAt: status.created_at,
        isReply: !!contentStatus.in_reply_to_id,
        embed: mapMastodonEmbed(contentStatus),
        quotedPost,
        metrics: {
            likeCount: contentStatus.favourites_count ?? 0,
            repostCount: contentStatus.reblogs_count ?? 0,
            replyCount: contentStatus.replies_count ?? 0,
        },
        author: isBoost ? author : (profileAuthor ?? author),
        boostedBy,
        likeUri: contentStatus.favourited ? `mastodon:favourited:${contentStatus.id}` : null,
        repostUri: contentStatus.reblogged ? `mastodon:reblogged:${contentStatus.id}` : null,
        bookmarkUri: contentStatus.bookmarked ? `mastodon:bookmarked:${contentStatus.id}` : null,
        // Mastodon only serialises `pinned` when the status is pinnable by the viewer,
        // so an absent field means "unknown" and must not become `false`.
        ...(typeof contentStatus.pinned === 'boolean' && { isPinned: contentStatus.pinned }),
        isActivityPub: true,
        url: contentStatus.url ?? undefined,
        contentWarning: contentWarningFromMastodon(
            contentStatus.sensitive,
            contentStatus.spoiler_text
        ),
        poll: contentStatus.poll ? mapMastodonPoll(contentStatus.poll) : undefined,
    }
}

/**
 * Map media attachments and cards from a Mastodon status to our PostEmbed type.
 * Images take precedence over cards (same as GraphQL mapper).
 */
export function mapMastodonEmbed(status: MastodonStatus): PostEmbed | undefined {
    const images = status.media_attachments.filter(a => a.type === 'image')
    if (images.length > 0) {
        return {
            type: 'images',
            images: images.map(img => ({
                url: img.preview_url || img.url,
                fullsize: img.url,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty description is meaningfully "no alt text", same as missing
                alt: img.description || undefined,
                aspectRatio:
                    img.meta?.original?.width && img.meta?.original?.height
                        ? { width: img.meta.original.width, height: img.meta.original.height }
                        : undefined,
            })),
        }
    }

    const video = status.media_attachments.find(a => a.type === 'video' || a.type === 'gifv')
    if (video) {
        return {
            type: 'video',
            video: {
                playlist: video.url,
                thumbnail: video.preview_url || undefined,
                aspectRatio:
                    video.meta?.original?.width && video.meta?.original?.height
                        ? { width: video.meta.original.width, height: video.meta.original.height }
                        : undefined,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty description is meaningfully "no alt text", same as missing
                alt: video.description || undefined,
            },
        }
    }

    if (status.card) {
        return {
            type: 'external',
            external: {
                uri: status.card.url,
                title: status.card.title || undefined,
                description: status.card.description || undefined,
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty image URL is invalid, same as missing
                thumb: status.card.image || undefined,
            },
        }
    }

    return undefined
}

/**
 * Map a Mastodon API Poll to the normalized PostPoll type.
 */
export function mapMastodonPoll(poll: MastodonPoll): PostPoll {
    return {
        id: poll.id,
        expiresAt: poll.expires_at,
        expired: poll.expired,
        multiple: poll.multiple,
        totalVotes: poll.votes_count,
        totalVoters: poll.voters_count,
        voted: poll.voted ?? undefined,
        ownVotes: poll.own_votes ?? [],
        options: poll.options.map(o => ({
            title: o.title,
            votesCount: o.votes_count,
        })),
    }
}

/**
 * Resolve a remote status on the home instance via the v2 search API with resolve=true.
 *
 * Federates the lookup from the user's home instance, returning the local copy of the
 * status with authenticated state (voted, favourited, own_votes, etc.).
 *
 * @param session - Validated Mastodon session (access_token must be current)
 * @param postUri - The canonical URI of the post to resolve
 * @returns The resolved MastodonStatus, or null if not found on the home instance
 */
async function resolveRemoteStatus(
    session: MastodonSession,
    postUri: string
): Promise<MastodonStatus | null> {
    const searchParams = new URLSearchParams({
        q: postUri,
        resolve: 'true',
        type: 'statuses',
    })

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v2/search?${searchParams.toString()}`,
            {
                headers: { Authorization: `Bearer ${session.access_token}` },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to resolve post on home instance: ${res.status}`)
        }

        const data: { statuses: MastodonStatus[] } = await res.json()
        return data.statuses[0] ?? null
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Resolve the local status ID for a post on the user's home instance.
 *
 * For local posts (post origin matches session instance), returns fallbackId
 * directly without a network request. For remote posts, searches the home
 * instance with resolve=true to federate the lookup and get the local ID.
 *
 * This is required for engagement actions (favourite, reblog, bookmark) on
 * remote posts — the remote instance's status ID is not valid on the home
 * instance.
 *
 * @param session - Mastodon session
 * @param postUri - Canonical ActivityPub URI of the post (e.g. "https://twit.social/users/...")
 * @param fallbackId - The status ID from the post's origin instance
 * @returns The local status ID on the home instance
 */
export async function resolveLocalStatusId(
    session: MastodonSession,
    postUri: string,
    fallbackId: string
): Promise<string> {
    if (postUri.startsWith('at://')) {
        throw new Error('Cannot resolve AT Protocol URI as a Mastodon status ID')
    }

    try {
        const sessionOrigin = new URL(session.instance_url).origin
        const postOrigin = new URL(postUri).origin
        if (sessionOrigin === postOrigin) {
            return fallbackId
        }
    } catch {
        // If URL parsing fails, fall through to resolution
    }

    const validSession = await ensureValidSession(session)
    const resolved = await resolveRemoteStatus(validSession, postUri)
    if (!resolved) {
        throw new Error('Post not found on home instance — cannot perform action')
    }
    return resolved.id
}

/**
 * Vote on a poll on the user's Mastodon instance.
 *
 * Resolves the post on the home instance first (via the search API with resolve=true)
 * to obtain the home instance's local poll ID. This is required when the post originated
 * on a remote instance — the remote poll.id is not valid on the home instance.
 *
 * @param mastodonSession - Authenticated Mastodon session
 * @param postUrl - Original post URI (e.g. https://cosocial.ca/users/evan/statuses/...)
 * @param choices - Array of option indices to vote for
 * @returns Updated PostPoll with voted: true and ownVotes populated
 */
export async function voteOnMastodonPoll(
    mastodonSession: MastodonSession,
    postUrl: string,
    choices: number[]
): Promise<PostPoll> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    // Step 1: Resolve the post on the home instance to get its local poll ID.
    // Posts from remote instances have a poll.id scoped to that remote instance;
    // we must search with resolve=true to federate the lookup and get the local ID.
    const resolved = await resolveRemoteStatus(session, postUrl)
    if (!resolved) {
        throw new Error('Post not found on home instance — cannot vote')
    }
    if (!resolved.poll) {
        throw new Error('Resolved post has no poll')
    }
    const localPollId = resolved.poll.id

    // Step 2: Vote using the home instance's local poll ID.
    const voteController = new AbortController()
    const voteTimeout = setTimeout(() => voteController.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/polls/${encodeURIComponent(localPollId)}/votes`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ choices }),
                signal: voteController.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to vote on poll: ${res.status}`)
        }

        const updatedPoll: MastodonPoll = await res.json()
        return mapMastodonPoll(updatedPoll)
    } finally {
        clearTimeout(voteTimeout)
    }
}

/**
 * Map a Mastodon quote to a normalized Post, if the quote is accepted.
 * Returns undefined for non-accepted quotes or missing data.
 *
 * fetchedInstanceUrl is the instance the enclosing status JSON was fetched from and
 * is the only source for a local-form `acct`'s domain; `quote.quoted_status.uri` is
 * remote-controlled and must never be consulted for identity.
 */
function mapMastodonQuote(
    quote: MastodonQuote | null | undefined,
    fetchedInstanceUrl: string,
    isTrustedOrigin = true
): Post | undefined {
    if (!quote || quote.state !== 'accepted' || !quote.quoted_status) return undefined

    const qs = quote.quoted_status
    const qsText = stripHTML(qs.content)
    const qsBridge = deriveATBridgeFromAcct(qs.account.acct, qs.account.username, isTrustedOrigin)
    const qsNormalizedHandle = qsBridge.isBridged
        ? qsBridge.handle
        : normalizeAcct(qs.account.acct, fetchedInstanceUrl)

    return {
        uri: qs.uri,
        cid: undefined,
        rkey: undefined,
        mastodonStatusId: qs.id,
        text: qsText,
        createdAt: qs.created_at,
        isReply: false,
        embed: mapMastodonEmbed(qs),
        metrics: {
            likeCount: qs.favourites_count ?? 0,
            repostCount: qs.reblogs_count ?? 0,
            replyCount: qs.replies_count ?? 0,
        },
        author: {
            did: '',
            handle: qsNormalizedHandle,
            displayName: qs.account.display_name || undefined,
            avatarUrl: qs.account.avatar || undefined,
            bridgeStatus: qsBridge.isBridged
                ? {
                      isBridged: true,
                      sourceProtocol: 'atprotocol',
                      activityPubHandle: qsBridge.activityPubHandle,
                  }
                : undefined,
        },
        likeUri: null,
        repostUri: null,
        isActivityPub: true,
    }
}

/**
 * Fetch profile posts directly from a Mastodon instance.
 *
 * Parses the handle, looks up the account ID, fetches statuses, and maps them.
 * Account IDs are cached in-memory to avoid redundant lookups during pagination.
 */
export async function fetchMastodonProfilePosts(
    handle: string,
    options?: {
        cursor?: string
        limit?: number
        mastodonSession?: MastodonSession
        onlyMedia?: boolean
        onlyReblogs?: boolean
        /**
         * Override the instance URL used for the account lookup and status fetch.
         * When provided, the account is looked up on this instance (using the full
         * handle as `acct`), and statuses are fetched from this instance with the
         * session's access token. Used by fetchAPPosts to route all AP profile
         * fetches through the user's own Mastodon instance, which has federated
         * data and returns engagement state (favourited/reblogged).
         */
        instanceOverride?: string
        /**
         * Prepend the account's pinned statuses to page one. Ignored once a cursor is
         * set or when a media/reblog-filtered view is requested (Posts tab only).
         */
        includePinned?: boolean
    }
): Promise<PostsPage> {
    // When instanceOverride is provided (e.g. the user's session instance), look up
    // the target account there. Mastodon instances have federated profile data, so
    // the lookup succeeds and statuses include engagement state since we're authenticated.
    const instanceUrl = options?.instanceOverride ?? instanceUrlFromHandle(handle)

    // The account/status JSON below may come directly from the viewed profile's own
    // (potentially hostile) instance rather than a party we trust to have done real
    // webfinger-based discovery — see apBridgedIdentity.ts's module docblock (#2226
    // follow-up). Only honor the acct/username bridge cross-check when the fetch is
    // known-trusted: instanceOverride is only ever set by callers to the user's own
    // authenticated session instance, and isTrustedBridgeOrigin separately allows
    // Bridgy Fed's own server.
    const isTrustedOrigin = isTrustedBridgeOrigin(
        instanceUrl,
        options?.mastodonSession?.instance_url
    )

    // Strip leading @ if present (e.g., "@alice@mastodon.social" → "alice")
    const username = handle.replace(/^@/, '').split('@')[0]

    // When routing through an instance other than the profile's own home
    // instance (e.g. viewing a remote account via the session's instance),
    // look up by full handle so the remote account is resolved via
    // federation (e.g. "alice@other.social"). When the override instance IS
    // the profile's own home instance — most commonly when viewing your own
    // profile — the account is local there, so username-only lookup works
    // and shares a cache entry/request with other same-instance,
    // username-only lookups (e.g. fetchAPProfileStats) instead of duplicating
    // the request under a different `acct` spelling.
    const isRemoteInstanceOverride =
        options?.instanceOverride !== undefined &&
        !isSameCanonicalOrigin(options.instanceOverride, instanceUrlFromHandle(handle))
    const lookupAcct = isRemoteInstanceOverride ? handle.replace(/^@/, '') : username

    // Check account cache (includes display name and avatar for profileAuthor)
    const cacheKey = `${instanceUrl}:${lookupAcct}`
    let cached = accountCache.get(cacheKey)

    if (!cached) {
        const account = await lookupAccountOnInstance(instanceUrl, lookupAcct)
        const resolvedHandle = resolveAcctHandle(
            account.acct,
            instanceUrl,
            account.username,
            isTrustedOrigin
        )
        cached = {
            id: account.id,
            displayName: account.display_name || undefined,
            avatarUrl: account.avatar || undefined,
            resolvedHandle,
        }
        setCachedAccount(cacheKey, cached)
    }

    // Parse cursor: for the first page, cursor is undefined.
    // For subsequent pages, cursor is the max_id extracted from the Link header.
    const fetchOptions: {
        maxId?: string
        limit?: number
        accessToken?: string
        onlyMedia?: boolean
        onlyReblogs?: boolean
    } = {
        limit: options?.limit ?? DEFAULT_LIMIT,
    }
    if (options?.cursor) {
        fetchOptions.maxId = options.cursor
    }
    if (options?.onlyMedia) {
        fetchOptions.onlyMedia = true
    }
    if (options?.onlyReblogs) {
        fetchOptions.onlyReblogs = true
    }

    if (options?.mastodonSession) {
        try {
            if (options.instanceOverride) {
                // instanceOverride always points to the session instance — always authenticate.
                // This ensures engagement state (favourited/reblogged) is returned for all
                // AP profile fetches routed through the user's instance.
                fetchOptions.accessToken = options.mastodonSession.access_token
            } else {
                // Use authenticated fetch when the profile is on the user's own instance
                // so the API returns engagement state (favourited/reblogged).
                const sessionInstanceUrl = new URL(options.mastodonSession.instance_url).origin
                const profileInstanceUrl = new URL(instanceUrl).origin
                if (sessionInstanceUrl === profileInstanceUrl) {
                    fetchOptions.accessToken = options.mastodonSession.access_token
                }
            }
        } catch {
            console.warn('[mastodon] Failed to parse instance URL for authenticated fetch')
        }
    }

    // Pinned ("featured") statuses are a separate request, issued only for page one of
    // the Posts tab. A failure there must never blank the profile, so it degrades to [].
    const wantsPinned =
        !!options?.includePinned && !options.cursor && !options.onlyMedia && !options.onlyReblogs

    const [{ statuses, nextUrl }, { statuses: pinnedStatuses }] = await Promise.all([
        fetchMastodonStatuses(instanceUrl, cached.id, fetchOptions),
        wantsPinned
            ? fetchMastodonStatuses(instanceUrl, cached.id, {
                  accessToken: fetchOptions.accessToken,
                  pinned: true,
              }).catch((err: unknown) => {
                  console.warn('[mastodon] pinned fetch failed:', err)
                  return { statuses: [] as MastodonStatus[] }
              })
            : Promise.resolve({ statuses: [] as MastodonStatus[] }),
    ])

    // Use the resolved handle from the account lookup (handles DID-based accts, AT→AP bridges,
    // and regular AP users). Fall back to username if the cache was populated before this field
    // was added, or to the full handle for non-domain usernames.
    const profileHandle = cached.resolvedHandle ?? (username.includes('.') ? username : handle)

    const profileAuthor: Post['author'] = {
        did: '',
        handle: profileHandle,
        displayName: cached.displayName,
        avatarUrl: cached.avatarUrl,
    }

    // Pinned entries lead the page in server order (newest pin first). The chronological
    // copies stay in the normal list — Mastodon shows both, and we do not dedupe.
    const pinnedPosts = pinnedStatuses.map(s => ({
        ...mapMastodonStatus(s, instanceUrl, profileAuthor, isTrustedOrigin),
        pinnedEntry: true,
        isPinned: true,
    }))

    const posts = [
        ...pinnedPosts,
        ...statuses.map(s => mapMastodonStatus(s, instanceUrl, profileAuthor, isTrustedOrigin)),
    ]

    // Extract cursor from the next page URL's max_id param
    let cursor: string | undefined
    if (nextUrl) {
        try {
            const nextUrlParams = new URL(nextUrl).searchParams
            cursor = nextUrlParams.get('max_id') ?? undefined
        } catch {
            // If URL parsing fails, use the last status ID as cursor
            if (statuses.length > 0) {
                cursor = statuses[statuses.length - 1].id
            }
        }
    }

    return { posts, cursor }
}

// --- Shared Fetch Helper ---

/**
 * Fetch a JSON response from a Mastodon instance endpoint with timeout and SSRF protection.
 */
async function fetchMastodonEndpoint<T>(
    instanceUrl: string,
    path: string,
    accessToken?: string
): Promise<T> {
    const hostname = new URL(instanceUrl).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const headers: Record<string, string> = {}
        if (accessToken) {
            headers.Authorization = `Bearer ${accessToken}`
        }
        const res = await fetch(`${instanceUrl}${path}`, { headers, signal: controller.signal })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        return res.json() as Promise<T>
    } finally {
        clearTimeout(timeout)
    }
}

// --- Thread Fetching ---

/**
 * Fetch a single status by ID from a Mastodon instance (unauthenticated).
 */
export async function fetchMastodonStatusById(
    instanceUrl: string,
    statusId: string,
    accessToken?: string
): Promise<MastodonStatus> {
    return fetchMastodonEndpoint<MastodonStatus>(
        instanceUrl,
        `/api/v1/statuses/${encodeURIComponent(statusId)}`,
        accessToken
    )
}

/**
 * Fetch thread context (ancestors + descendants) for a status.
 * When accessToken is provided, the response includes favourited/reblogged state.
 */
export async function fetchMastodonContext(
    instanceUrl: string,
    statusId: string,
    accessToken?: string
): Promise<MastodonContext> {
    return fetchMastodonEndpoint<MastodonContext>(
        instanceUrl,
        `/api/v1/statuses/${encodeURIComponent(statusId)}/context`,
        accessToken
    )
}

/**
 * Compute reply depth and replyingTo metadata for descendants.
 *
 * Mastodon returns descendants as a flat list. We build a depth map from
 * in_reply_to_id chains, starting at the main post (depth 0).
 */
export function buildThreadReplies(
    mainStatusId: string,
    descendants: MastodonStatus[],
    fetchedInstanceUrl: string,
    isTrustedOrigin = true
): ThreadPost[] {
    // Map status ID → status for quick lookup
    const statusById = new Map<string, MastodonStatus>()
    for (const s of descendants) {
        statusById.set(s.id, s)
    }

    // Map status ID → depth (main post's direct replies are depth 0)
    const depthMap = new Map<string, number>()
    depthMap.set(mainStatusId, -1) // Main post itself is "above" depth 0

    // Compute depths in a single pass (Mastodon returns descendants in chronological order,
    // so parents always appear before their children)
    for (const s of descendants) {
        const parentId = s.in_reply_to_id
        if (parentId && depthMap.has(parentId)) {
            depthMap.set(s.id, depthMap.get(parentId)! + 1)
        } else {
            // Parent not in our set — treat as direct reply to main post
            depthMap.set(s.id, 0)
        }
    }

    return descendants.map(s => {
        const depth = depthMap.get(s.id) ?? 0

        // Build replyingTo for replies that aren't direct replies to the main post
        let replyingTo: ThreadPost['replyingTo']
        if (s.in_reply_to_id && s.in_reply_to_id !== mainStatusId) {
            const parent = statusById.get(s.in_reply_to_id)
            if (parent) {
                const parentNormalized = resolveAcctHandle(
                    parent.account.acct,
                    fetchedInstanceUrl,
                    parent.account.username,
                    isTrustedOrigin
                )
                replyingTo = {
                    uri: parent.uri,
                    authorHandle: parentNormalized,
                    authorDisplayName: parent.account.display_name || undefined,
                }
            }
        }

        return {
            post: mapMastodonStatus(s, fetchedInstanceUrl, undefined, isTrustedOrigin),
            replyDepth: depth,
            replyingTo,
        }
    })
}

/**
 * Fetch a complete AP thread directly from a Mastodon instance.
 *
 * Makes two API calls: one for the status itself, one for the context
 * (ancestors + descendants). This replaces N+1 GraphQL queries with 2 HTTP calls.
 *
 * @param instanceUrl - The instance base URL (e.g., "https://mastodon.social")
 * @param statusId - The numeric Mastodon status ID
 */
export async function fetchMastodonThread(
    instanceUrl: string,
    statusId: string,
    mastodonSession?: MastodonSession
): Promise<Thread> {
    // Use authenticated requests when the thread is on the user's own instance
    // so the API returns favourited/reblogged engagement state.
    let accessToken: string | undefined
    let validatedSession: MastodonSession | undefined
    if (mastodonSession) {
        try {
            const session = await ensureValidSession(mastodonSession)
            validatedSession = session
            const sessionOrigin = new URL(session.instance_url).origin
            const threadOrigin = new URL(instanceUrl).origin
            if (sessionOrigin === threadOrigin) {
                accessToken = session.access_token
            }
        } catch {
            // Fall through to unauthenticated fetch
        }
    }

    // The account/status JSON below may come directly from the thread's own
    // (potentially hostile) instance rather than a party we trust to have done real
    // webfinger-based discovery — see apBridgedIdentity.ts's module docblock (#2226
    // follow-up). Only honor the acct/username bridge cross-check when the fetch is
    // known-trusted: the same-origin check mirrors the accessToken gate above, and
    // isTrustedBridgeOrigin separately allows Bridgy Fed's own server.
    const isTrustedOrigin = isTrustedBridgeOrigin(instanceUrl, mastodonSession?.instance_url)

    // Fetch the main status and context in parallel
    const [mainStatus, context] = await Promise.all([
        fetchMastodonStatusById(instanceUrl, statusId, accessToken),
        fetchMastodonContext(instanceUrl, statusId, accessToken),
    ])

    const mainPost = mapMastodonStatus(mainStatus, instanceUrl, undefined, isTrustedOrigin)

    // Map ancestors to parent chain (already in chronological order from API)
    const parentChain: ThreadPost[] = context.ancestors.map(s => ({
        post: mapMastodonStatus(s, instanceUrl, undefined, isTrustedOrigin),
        replyDepth: 0,
        replyingTo: undefined,
    }))

    // Build reply list with computed depths
    const replies = buildThreadReplies(
        mainStatus.id,
        context.descendants,
        instanceUrl,
        isTrustedOrigin
    )

    const thread: Thread = {
        post: {
            post: mainPost,
            replyDepth: 0,
            replyingTo: undefined,
        },
        parentChain,
        replies,
        replyCount: mainStatus.replies_count ?? 0,
    }

    // Enrich remote threads: the origin instance returns unauthenticated poll state
    // (voted: null, own_votes: []). Resolve each post with a poll on the home instance
    // to get the authenticated state, then merge it back.
    if (validatedSession && !accessToken) {
        const allThreadPosts = [thread.post, ...thread.parentChain, ...thread.replies]
        const pollPosts = allThreadPosts.filter(tp => tp.post.poll)
        if (pollPosts.length > 0) {
            await Promise.all(
                pollPosts.map(async tp => {
                    try {
                        const resolvedStatus = await resolveRemoteStatus(
                            validatedSession!,
                            tp.post.uri
                        )
                        if (resolvedStatus?.poll) {
                            tp.post.poll = mapMastodonPoll(resolvedStatus.poll)
                        }
                    } catch {
                        // Best-effort enrichment — don't block thread render on failure
                    }
                })
            )
        }
    }

    return thread
}

// --- Status Management ---

/**
 * Delete a status on the user's Mastodon instance.
 *
 * Requires authenticated session — the status must belong to the authenticated user.
 */
export async function deleteMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/statuses/${encodeURIComponent(statusId)}`,
            {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to delete status: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

// --- Engagement Actions ---

/**
 * Perform an authenticated action on a Mastodon status (favourite, reblog, etc.).
 *
 * Validates session, applies SSRF protection, and sends a POST request.
 */
type MastodonStatusAction =
    | 'favourite'
    | 'unfavourite'
    | 'reblog'
    | 'unreblog'
    | 'bookmark'
    | 'unbookmark'
    | 'pin'
    | 'unpin'

/**
 * A status action rejected by the instance, carrying the HTTP status.
 *
 * Pin failures need the status code: Mastodon answers `422` for every pin
 * validation failure (limit reached, ineligible visibility, not the owner's
 * status) and its `error` body is a localised string, so the code is the only
 * reliable signal. The message text is unchanged from the plain Error it
 * replaces, so existing assertions still match.
 */
export class MastodonStatusActionError extends Error {
    readonly action: string
    readonly status: number

    constructor(action: string, status: number) {
        super(`Failed to ${action} status: ${status}`)
        this.name = 'MastodonStatusActionError'
        this.action = action
        this.status = status
    }
}

async function mastodonStatusAction(
    mastodonSession: MastodonSession,
    statusId: string,
    action: MastodonStatusAction
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/statuses/${encodeURIComponent(statusId)}/${action}`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new MastodonStatusActionError(action, res.status)
        }
    } finally {
        clearTimeout(timeout)
    }
}

/** Favourite a status on the user's Mastodon instance. */
export async function favouriteMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'favourite')
}

/** Unfavourite a status on the user's Mastodon instance. */
export async function unfavouriteMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'unfavourite')
}

/** Reblog a status on the user's Mastodon instance. */
export async function reblogMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'reblog')
}

/** Unreblog a status on the user's Mastodon instance. */
export async function unreblogMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'unreblog')
}

/** Bookmark a status on the user's Mastodon instance. */
export async function bookmarkMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'bookmark')
}

/** Pin a status to the user's Mastodon profile. Throws with status 422 when rejected. */
export async function pinMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'pin')
}

/** Unpin a status from the user's Mastodon profile. */
export async function unpinMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'unpin')
}

/** Remove a bookmark from a status on the user's Mastodon instance. */
export async function unbookmarkMastodonStatus(
    mastodonSession: MastodonSession,
    statusId: string
): Promise<void> {
    return mastodonStatusAction(mastodonSession, statusId, 'unbookmark')
}

/**
 * Shared bookmark fetch for Mastodon-API-compatible instances (Mastodon and Pixelfed).
 *
 * Accepts a pre-validated session (instance_url + access_token), a status mapper,
 * and a bookmark URI prefix so callers can supply protocol-specific mapping logic.
 */
export async function fetchAPBookmarks(
    validSession: { instance_url: string; access_token: string },
    options: { cursor?: string; limit?: number } | undefined,
    mapStatus: (s: MastodonStatus) => Post,
    bookmarkUriPrefix: string
): Promise<PostsPage> {
    const parsed = new URL(validSession.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${validSession.instance_url}/api/v1/bookmarks?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${validSession.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`API error: ${res.status}`)
        }

        const statuses: MastodonStatus[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        // Posts from bookmarks endpoint are always bookmarked
        const posts = statuses.map(s => ({
            ...mapStatus(s),
            bookmarkUri: `${bookmarkUriPrefix}:bookmarked:${s.id}`,
        }))

        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the authenticated user's bookmarked statuses.
 *
 * Uses the same session validation, SSRF protection, and pagination pattern
 * as fetchMastodonFavourites.
 */
export async function fetchMastodonBookmarks(
    mastodonSession: MastodonSession,
    options?: { cursor?: string; limit?: number }
): Promise<PostsPage> {
    const session = await ensureValidSession(mastodonSession)
    return fetchAPBookmarks(
        session,
        options,
        s => mapMastodonStatus(s, session.instance_url),
        'mastodon'
    )
}

/** Block a Mastodon account from the user's home instance. */
export async function blockMastodonUser(
    mastodonSession: MastodonSession,
    accountId: string
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/block`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to block account: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

export interface MastodonBlockedAccount {
    id: string
    handle: string
    displayName?: string
    avatar?: string
}

export interface MastodonBlockedAccountsPage {
    accounts: MastodonBlockedAccount[]
    cursor?: string
}

/** Fetch the list of accounts blocked by the authenticated user. */
export async function fetchMastodonBlocks(
    mastodonSession: MastodonSession,
    options?: { cursor?: string; limit?: number }
): Promise<MastodonBlockedAccountsPage> {
    const session = await ensureValidSession(mastodonSession)

    let parsed: URL
    try {
        parsed = new URL(session.instance_url)
    } catch {
        throw new Error(`Invalid instance URL: ${session.instance_url}`)
    }
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.cursor) {
        params.set('max_id', options.cursor)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${session.instance_url}/api/v1/blocks?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${session.access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Mastodon API error: ${res.status}`)
        }

        const rawAccounts: MastodonAccount[] = await res.json()
        const nextUrl = parseLinkHeader(res.headers.get('Link'))

        const accounts: MastodonBlockedAccount[] = rawAccounts.map(a => ({
            id: a.id,
            handle: normalizeAcct(a.acct, session.instance_url),
            displayName: a.display_name || undefined,
            avatar: a.avatar || undefined,
        }))

        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === parsed.origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor =
                        rawAccounts.length > 0 ? rawAccounts[rawAccounts.length - 1].id : undefined
                }
            } catch {
                if (rawAccounts.length > 0) {
                    cursor = rawAccounts[rawAccounts.length - 1].id
                }
            }
        }

        return { accounts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}

/** Unblock a Mastodon account from the user's home instance. */
export async function unblockMastodonUser(
    mastodonSession: MastodonSession,
    accountId: string
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)

    const parsed = new URL(session.instance_url)
    if (parsed.protocol !== 'https:') {
        throw new Error(`Unsafe URL scheme: ${parsed.protocol}`)
    }
    if (!isSafeHostname(parsed.hostname)) {
        throw new Error(`Unsafe hostname: ${parsed.hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const res = await fetch(
            `${session.instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/unblock`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.access_token}`,
                },
                signal: controller.signal,
            }
        )

        if (!res.ok) {
            throw new Error(`Failed to unblock account: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Clear the account cache (useful for testing)
 */
export function clearAccountCache(): void {
    accountCache.clear()
}

/**
 * Fetch the authenticated user's Mastodon lists.
 */
export async function fetchMastodonLists(
    mastodonSession: MastodonSession
): Promise<MastodonList[]> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch Mastodon lists: ${res.status}`)
        }

        const data = (await res.json()) as Array<{ id: string; title: string }>
        return data.map(list => ({ id: list.id, title: list.title }))
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch the lists (owned by the authenticated user) that contain the given account.
 * Used to check list membership for a profile without paging every list's members.
 */
export async function fetchMastodonAccountLists(
    mastodonSession: MastodonSession,
    accountId: string
): Promise<MastodonList[]> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/accounts/${encodeURIComponent(accountId)}/lists`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch account lists: ${res.status}`)
        }

        const data: unknown = await res.json()
        if (!Array.isArray(data)) {
            throw new Error('Unexpected response format for account lists')
        }

        const lists: MastodonList[] = []
        for (const item of data) {
            if (
                item &&
                typeof item === 'object' &&
                typeof (item as { id?: unknown }).id === 'string' &&
                typeof (item as { title?: unknown }).title === 'string'
            ) {
                lists.push({
                    id: (item as { id: string }).id,
                    title: (item as { title: string }).title,
                })
            }
        }
        return lists
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Create a new Mastodon list.
 */
export async function createMastodonList(
    mastodonSession: MastodonSession,
    title: string
): Promise<MastodonList> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists`
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ title }),
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to create Mastodon list: ${res.status}`)
        }

        const data = (await res.json()) as { id: string; title: string }
        return { id: data.id, title: data.title }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Delete a Mastodon list.
 */
export async function deleteMastodonList(
    mastodonSession: MastodonSession,
    listId: string
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists/${encodeURIComponent(listId)}`
        const res = await fetch(url, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to delete Mastodon list: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Update a Mastodon list title.
 */
export async function updateMastodonList(
    mastodonSession: MastodonSession,
    listId: string,
    title: string
): Promise<MastodonList> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists/${encodeURIComponent(listId)}`
        const res = await fetch(url, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ title }),
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to update Mastodon list: ${res.status}`)
        }

        const data = (await res.json()) as { id: string; title: string }
        return { id: data.id, title: data.title }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch accounts in a Mastodon list.
 */
export async function fetchMastodonListMembers(
    mastodonSession: MastodonSession,
    listId: string
): Promise<
    Array<{ id: string; username: string; display_name: string; avatar: string; acct: string }>
> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists/${encodeURIComponent(listId)}/accounts?limit=80`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch list members: ${res.status}`)
        }

        return (await res.json()) as Array<{
            id: string
            username: string
            display_name: string
            avatar: string
            acct: string
        }>
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Add accounts to a Mastodon list.
 */
export async function addToMastodonList(
    mastodonSession: MastodonSession,
    listId: string,
    accountIds: string[]
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists/${encodeURIComponent(listId)}/accounts`
        const res = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ account_ids: accountIds }),
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to add accounts to list: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Remove accounts from a Mastodon list.
 */
export async function removeFromMastodonList(
    mastodonSession: MastodonSession,
    listId: string,
    accountIds: string[]
): Promise<void> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/lists/${encodeURIComponent(listId)}/accounts`
        const res = await fetch(url, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ account_ids: accountIds }),
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to remove accounts from list: ${res.status}`)
        }
    } finally {
        clearTimeout(timeout)
    }
}

export interface MastodonAccountSearchResult {
    id: string
    username: string
    display_name: string
    avatar: string
    acct: string
}

/**
 * Search for Mastodon accounts by query string.
 */
export async function searchMastodonAccounts(
    mastodonSession: MastodonSession,
    query: string,
    limit = 10
): Promise<MastodonAccountSearchResult[]> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const params = new URLSearchParams({
            q: query,
            type: 'accounts',
            limit: String(limit),
        })
        const url = `${instance_url}/api/v2/search?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Search failed: ${res.status}`)
        }

        const data = (await res.json()) as { accounts?: MastodonAccountSearchResult[] }
        return data.accounts ?? []
    } finally {
        clearTimeout(timeout)
    }
}

/**
 * Fetch posts from a Mastodon list timeline.
 */
export async function fetchMastodonListTimeline(
    mastodonSession: MastodonSession,
    listId: string,
    options?: { maxId?: string; limit?: number }
): Promise<PostsPage> {
    const session = await ensureValidSession(mastodonSession)
    const { instance_url, access_token } = session

    const hostname = new URL(instance_url).hostname
    if (!isSafeHostname(hostname)) {
        throw new Error(`Unsafe hostname: ${hostname}`)
    }

    const params = new URLSearchParams()
    params.set('limit', String(options?.limit ?? DEFAULT_LIMIT))
    if (options?.maxId) {
        params.set('max_id', options.maxId)
    }

    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)

    try {
        const url = `${instance_url}/api/v1/timelines/list/${encodeURIComponent(listId)}?${params}`
        const res = await fetch(url, {
            headers: { Authorization: `Bearer ${access_token}` },
            signal: controller.signal,
        })

        if (!res.ok) {
            throw new Error(`Failed to fetch list timeline: ${res.status}`)
        }

        const statuses = (await res.json()) as MastodonStatus[]
        const nextUrl = parseLinkHeader(res.headers.get('Link'))
        let cursor: string | undefined
        if (nextUrl) {
            try {
                const nextParsed = new URL(nextUrl)
                if (nextParsed.origin === new URL(instance_url).origin) {
                    cursor = nextParsed.searchParams.get('max_id') ?? undefined
                } else {
                    cursor = statuses.length > 0 ? statuses[statuses.length - 1].id : undefined
                }
            } catch {
                if (statuses.length > 0) {
                    cursor = statuses[statuses.length - 1].id
                }
            }
        }

        const posts = statuses.map(s => mapMastodonStatus(s, instance_url))

        return { posts, cursor }
    } finally {
        clearTimeout(timeout)
    }
}
